import React from 'react'
import { Trans } from '@lingui/react/macro'
import { useLingui } from '@lingui/react/macro'

import { Card } from '@newComponents/Card'
import { Paragraph } from '@newComponents/Text'
import { Link } from '@newComponents/Link'

import { DocSection } from '../shared/DocSection'
import { DocScreenshot } from '../shared/DocScreenshot'
import { DocStep } from '../shared/DocStep'

const Tiers = () => {
  const { t } = useLingui()

  return (
    <>
      <Card gap='$4'>
        <Paragraph size='$5'>
          <Trans>
            Ticketing tiers let you offer different ticket options for an Event.
            For example, an Event might have a free General Admission tier
            alongside a paid VIP tier with extra perks, or an Early Bird tier at
            a discounted price that expires before the regular tier goes on
            sale.
          </Trans>
        </Paragraph>
        <DocScreenshot
          src='docs/events/event-page.png'
          alt={t`Event page where attendees select from available ticketing tiers during checkout`}
          caption={t`Attendees choose from available tiers on the Event page`}
        />
      </Card>

      <Card gap='$4'>
        <DocSection id='creating-tiers' title={t`Creating a Tier`}>
          <DocStep
            number={1}
            title={t`Open Event Settings`}
            description={t`Navigate to the Event you want to add tickets to and open Event Settings. You must have the Organizer or Editor role.`}
          />
          <DocStep
            number={2}
            title={t`Go to the Tickets section`}
            description={t`Select the Tickets tab in Event Settings to view and manage your ticketing tiers.`}
          />
          <DocStep
            number={3}
            title={t`Click Add Tier`}
            description={t`Click the Add Tier button to create a new ticketing tier.`}
          />
          <DocStep
            number={4}
            title={t`Set the name, description, and price`}
            description={t`Give the tier a clear name such as General Admission or VIP, add an optional description explaining what is included, and set the price. Set the price to $0 to create a free tier.`}
          />
          <DocStep
            number={5}
            title={t`Configure optional settings`}
            description={t`Optionally set a ticket limit to cap availability, define a sale window with start and end dates, set minimum and maximum tickets per order, and configure tax settings if applicable.`}
          />
          <DocStep
            number={6}
            title={t`Save and activate`}
            description={t`Save the tier to make it live. Attendees will see it on the Event page when they go to check out.`}
          />
        </DocSection>
      </Card>

      <Card gap='$4'>
        <DocSection id='managing-tiers' title={t`Managing Tiers`}>
          <Paragraph size='$5'>
            <Trans>
              You can create as many tiers as you need. Each tier tracks its
              sold count automatically so you can see remaining availability at
              a glance. Tiers can be paused or hidden at any time without
              deleting them, which is useful for ending early bird sales or
              holding back VIP tickets for a later release.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              Mixing free and paid tiers on the same Event is fully supported.
              This lets you offer a free option for community members while
              charging for premium experiences. Paid tiers require a connected
              Stripe account — see{' '}
              <Link href='/docs/events/stripe'>Setting Up Stripe</Link> for
              details.
            </Trans>
          </Paragraph>
          <Paragraph size='$5'>
            <Trans>
              To offer reduced pricing on specific tiers, create{' '}
              <Link href='/docs/events/discounts'>discount codes</Link> that can
              be applied during checkout.
            </Trans>
          </Paragraph>
        </DocSection>
      </Card>
    </>
  )
}

export default Tiers
